import { useCallback, useEffect, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useCommandState } from 'cmdk';
import { XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import { brainsEndpoints, desksEndpoints } from '@/api/endpoints';
import { Badge } from '@/components/ui/badge';
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import useDebounce from '@/hooks/useDebounce';
import { type Desks } from '@/hooks/useDesks';
import { selectAccountSlug } from '@/redux/session/selectors';
import { trackEvent } from '@/segment/segment';
import { type Brains } from '@/shared/types/brain';
import { type Desk } from '@/shared/types/desk';
import { EventName } from '@/shared/types/segment';

import { useCommandPaletteContext } from './CommandPaletteProvider';
import { AccountSwitchGroup } from './groups/AccountSwitchGroup';
import { AgentSubPagesGroup } from './groups/AgentSubPagesGroup';
import { DeskSubPagesGroup } from './groups/DeskSubPagesGroup';
import { EntitiesGroup } from './groups/EntitiesGroup';
import { EntityListGroup } from './groups/EntityListGroup';
import { NoResultsGroup } from './groups/NoResultsGroup';
import { PagesGroup } from './groups/PagesGroup';
import { QuickActionsGroup } from './groups/QuickActionsGroup';
import { RecentPagesGroup } from './groups/RecentPagesGroup';
import { SearchConversationDrillDown } from './groups/SearchConversationDrillDown';
import { UuidMatchGroup } from './groups/UuidMatchGroup';
import { useEntitySearch } from './hooks/useEntitySearch';
import { useFrecency } from './hooks/useFrecency';
import { useUuidLookup } from './hooks/useUuidLookup';
import { type DrillDown, type EntityResult } from './types';
import {
  ENTITY_TYPE_LABELS,
  type SelectType,
  getSelectType,
  PAGE_DRILL_DOWN_IDS,
  parseEntityValue,
} from './utils';

const DRILL_DOWN_NONE: DrillDown = { kind: 'none' };

/** Renders no-results UI only when cmdk's filter produces zero matches. */
const EmptyState = ({
  search,
  onAction,
  onConversationSearch,
}: {
  search: string;
  onAction: (action: () => void, navigateTo?: { path: string; label: string }) => void;
  onConversationSearch: () => void;
}) => {
  const filteredCount = useCommandState((state) => state.filtered.count);
  if (filteredCount > 0) {
    return null;
  }
  return (
    <NoResultsGroup
      search={search}
      onAction={onAction}
      onConversationSearch={onConversationSearch}
    />
  );
};

const CommandPaletteContent = ({ onClose }: { onClose: () => void }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const slug = useSelector(selectAccountSlug);
  const [search, setSearch] = useState('');
  const [drillDown, setDrillDown] = useState<DrillDown>(DRILL_DOWN_NONE);
  const debouncedSearch = useDebounce(search, 150); // 150ms = snappy UX
  const { recentPages } = useCommandPaletteContext();
  const { trackVisit, getScore } = useFrecency();
  const entityResults = useEntitySearch(debouncedSearch);
  const uuidLookup = useUuidLookup(search);
  const highlightedValue = useCommandState((state) => state.value);

  // Ref captures latest results so the debounced tracking effect reads the value at fire time
  const entityResultsRef = useRef(entityResults);
  entityResultsRef.current = entityResults;

  useEffect(() => {
    if (debouncedSearch.length < 2) {
      return;
    }
    const timer = setTimeout(() => {
      trackEvent(EventName.CommandPaletteSearch, {
        query_length: debouncedSearch.length,
        results_count: entityResultsRef.current.length,
      });
    }, 500);
    return () => clearTimeout(timer);
  }, [debouncedSearch]);

  const handleClose = useCallback(() => {
    setDrillDown(DRILL_DOWN_NONE);
    setSearch('');
    onClose();
  }, [onClose]);

  const handleSelect = useCallback(
    (path: string, label: string, opts?: { fullPath?: boolean; entityType?: string }) => {
      const { fullPath, entityType } = opts ?? {};
      if (!slug && !fullPath) {
        return;
      }
      const selectType: SelectType | 'uuid_match' = entityType
        ? 'uuid_match'
        : getSelectType(drillDown.kind === 'agent' || drillDown.kind === 'desk', highlightedValue);
      trackEvent(EventName.CommandPaletteSelect, {
        type: selectType,
        item: label,
        query_length: search.length,
        ...(entityType && { entity_type: entityType }),
      });
      trackVisit(path);
      const resolvedPath = fullPath ? path : `/${slug}${path}`;
      navigate(resolvedPath);
      handleClose();
    },
    [slug, navigate, trackVisit, handleClose, drillDown, search, highlightedValue]
  );

  const handleAction = useCallback(
    (action: () => void, navigateTo?: { path: string; label: string }) => {
      trackEvent(EventName.CommandPaletteSelect, {
        type: 'action',
        item: highlightedValue ?? '',
        query_length: search.length,
      });
      action();
      if (navigateTo && slug) {
        navigate(`/${slug}${navigateTo.path}`);
      }
      handleClose();
    },
    [handleClose, search, highlightedValue, navigate, slug]
  );

  const enterSearchConversationDrillDown = useCallback(() => {
    trackEvent(EventName.CommandPaletteDrillDown, { type: 'page_list', item: 'conversation' });
    setDrillDown({ kind: 'conversation' });

    // Clear the input only if the user typed a prefix of the action label itself
    // (e.g. "search" matching "Search conversations"). Otherwise keep it — it's
    // the user's actual query (e.g. "herman" → drill-down pre-filled with "herman").
    const actionLabel = t('command_palette.search_conversations').toLowerCase();
    if (actionLabel.startsWith(search.trim().toLowerCase())) {
      setSearch('');
    }
  }, [search, t]);

  const handleTabDrillDown = useCallback(
    (value: string): boolean => {
      const parsed = parseEntityValue(value);

      if (drillDown.kind === 'none') {
        // Check for page drill-down (Tab on a page item)
        const drillDownType = PAGE_DRILL_DOWN_IDS[value];
        if (drillDownType) {
          trackEvent(EventName.CommandPaletteDrillDown, { type: 'page_list', item: drillDownType });
          setDrillDown({ kind: 'page', type: drillDownType });
          setSearch('');
          return true;
        }

        // Check for entity drill-down (Tab on an entity result)
        if (!parsed) {
          return false;
        }

        if (parsed.type === 'ai_agent') {
          const agent = entityResults.find(
            (r: EntityResult) => r.id === parsed.id && r.type === 'ai_agent'
          );
          if (agent) {
            trackEvent(EventName.CommandPaletteDrillDown, { type: 'ai_agent', item: agent.name });
            setDrillDown({ kind: 'agent', brain_id: parsed.id, name: agent.name });
            setSearch('');
            return true;
          }
        } else if (parsed.type === 'environment') {
          const desksData = queryClient.getQueryData<Desks>([desksEndpoints.desks]);
          const desk = desksData?.desks?.find((d: Desk) => d.desk_id === parsed.id);
          if (desk) {
            trackEvent(EventName.CommandPaletteDrillDown, { type: 'environment', item: desk.name });
            setDrillDown({
              kind: 'desk',
              desk: {
                desk_id: desk.desk_id,
                name: desk.name,
                is_service_desk: desk.is_service_desk,
              },
            });
            setSearch('');
            return true;
          }
        }
        return false;
      }

      // When inside a page drill-down, Tab on an entity drills into sub-pages
      if (drillDown.kind === 'page') {
        if (parsed?.type === 'ai_agent') {
          const brainsData = queryClient.getQueryData<Brains>([brainsEndpoints.brains]);
          const brain = brainsData?.brains?.find((b) => b.brain_id === parsed.id);
          if (brain) {
            trackEvent(EventName.CommandPaletteDrillDown, { type: 'ai_agent', item: brain.name });
            setDrillDown({ kind: 'agent', brain_id: parsed.id, name: brain.name });
            setSearch('');
            return true;
          }
        }
      }
      return false;
    },
    [drillDown, entityResults, queryClient]
  );

  const handleBackspacePop = useCallback((): boolean => {
    if (drillDown.kind !== 'none') {
      setDrillDown(DRILL_DOWN_NONE);
      return true;
    }
    return false;
  }, [drillDown]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Tab' && highlightedValue && handleTabDrillDown(highlightedValue)) {
        e.preventDefault();
      }
      if (e.key === 'Backspace' && search === '' && handleBackspacePop()) {
        e.preventDefault();
      }
    },
    [search, highlightedValue, handleTabDrillDown, handleBackspacePop]
  );

  const hasSearch = search.length > 0;
  const hasDrillDown = drillDown.kind !== 'none';
  const isUuidSearch = uuidLookup.isLoading || !!uuidLookup.result;

  const activeDrillDownLabel = (() => {
    switch (drillDown.kind) {
      case 'agent':
        return `${t('common.ai_agent')}: ${drillDown.name}`;
      case 'desk':
        return `${t('common.environment')}: ${drillDown.desk.name}`;
      case 'page':
        return t(ENTITY_TYPE_LABELS[drillDown.type]);
      case 'conversation':
        return t('command_palette.search_conversations');
      case 'none':
        return null;
    }
  })();

  let placeholder: string;
  if (drillDown.kind === 'conversation') {
    placeholder = t('command_palette.conversation_search_hint');
  } else if (hasDrillDown) {
    placeholder = t('command_palette.type_to_filter');
  } else {
    placeholder = t('command_palette.placeholder');
  }

  return (
    <>
      {activeDrillDownLabel && (
        <div className="flex h-12 items-center gap-2 border-b px-3">
          <Badge
            asChild
            type="secondary"
            flavour="neutral"
            className="shrink-0 cursor-pointer"
            trailingIcon={<XIcon />}
          >
            <button type="button" onClick={() => setDrillDown(DRILL_DOWN_NONE)}>
              {activeDrillDownLabel}
            </button>
          </Badge>
        </div>
      )}
      <CommandInput
        placeholder={placeholder}
        value={search}
        onValueChange={setSearch}
        onKeyDown={handleKeyDown}
      />
      <CommandList>
        {drillDown.kind === 'agent' && (
          <AgentSubPagesGroup agent={drillDown} onSelect={handleSelect} />
        )}

        {drillDown.kind === 'desk' && (
          <DeskSubPagesGroup desk={drillDown.desk} onSelect={handleSelect} />
        )}

        {drillDown.kind === 'page' && (
          <EntityListGroup
            type={drillDown.type}
            onSelect={handleSelect}
            highlightedValue={highlightedValue}
            pageDrillDown={drillDown.type}
          />
        )}

        {drillDown.kind === 'conversation' && (
          <SearchConversationDrillDown
            search={search}
            onClose={handleClose}
            onNavigate={handleSelect}
          />
        )}

        {!hasDrillDown && (
          <>
            {!hasSearch && <RecentPagesGroup recentPages={recentPages} onSelect={handleSelect} />}

            {hasSearch && (
              <>
                <UuidMatchGroup
                  result={uuidLookup.result}
                  isLoading={uuidLookup.isLoading}
                  onSelect={handleSelect}
                />
                {uuidLookup.result && <CommandSeparator />}
                <EntitiesGroup
                  results={entityResults}
                  onSelect={handleSelect}
                  highlightedValue={highlightedValue}
                />
                <CommandSeparator />
                <PagesGroup
                  onSelect={handleSelect}
                  getScore={getScore}
                  highlightedValue={highlightedValue}
                />
                <CommandSeparator />
                <AccountSwitchGroup onSelect={handleSelect} />
              </>
            )}

            {!isUuidSearch && (
              <>
                <CommandSeparator />
                <QuickActionsGroup
                  onAction={handleAction}
                  onConversationSearch={enterSearchConversationDrillDown}
                />
              </>
            )}

            {hasSearch && (
              <EmptyState
                search={search}
                onAction={handleAction}
                onConversationSearch={enterSearchConversationDrillDown}
              />
            )}
          </>
        )}
      </CommandList>
    </>
  );
};

export const CommandPalette = () => {
  const { isOpen, setOpen } = useCommandPaletteContext();

  return (
    <CommandDialog open={isOpen} onOpenChange={setOpen}>
      <CommandPaletteContent onClose={() => setOpen(false)} />
    </CommandDialog>
  );
};
